import { useCallback, useEffect, useRef, useState } from 'react';
import { makeCodeForSite } from './codeGenerator';
import {
  LINE_HEIGHT,
  MAX_URL_ROWS,
  SCAN_FRACTION,
  SCROLL_SPEED,
  getSite,
} from './data';
import type { CodeLine, SiteData, TechKey, UrlRowData } from './types';

export function usePipelineAnimation() {
  const [urlRows, setUrlRows] = useState<UrlRowData[]>(() =>
    Array.from({ length: MAX_URL_ROWS }, (_, index) => ({
      host: getSite(index).host,
      color: getSite(index).color,
      status: 'idle' as const,
    }))
  );

  const [currentHost, setCurrentHost] = useState(getSite(0).host);
  const [codeLines, setCodeLines] = useState<CodeLine[]>([]);
  const [scanOffset, setScanOffset] = useState(-(LINE_HEIGHT * 3));
  const [flashingLines, setFlashingLines] = useState<Set<number>>(new Set());
  const [hitLines, setHitLines] = useState<Set<number>>(new Set());
  const [beamFlash, setBeamFlash] = useState(false);
  const [techsForSite, setTechsForSite] = useState<TechKey[]>([]);
  const [detectedTechs, setDetectedTechs] = useState<Set<TechKey>>(new Set());

  const viewportRef = useRef<HTMLDivElement>(null);
  const viewportHeightRef = useRef(300);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        viewportHeightRef.current = entry.contentRect.height;
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // All animation state lives in refs to avoid stale closures
  const scanOffsetRef = useRef(-(LINE_HEIGHT * 3));
  const lastTimestampRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const scanActiveRef = useRef(false);
  const detectedRef = useRef<Set<TechKey>>(new Set());
  const techsForSiteRef = useRef<TechKey[]>([]);
  const codeLinesRef = useRef<CodeLine[]>([]);
  const doneCallbackRef = useRef<(() => void) | null>(null);
  const siteIndexRef = useRef(0);
  const scanGenerationRef = useRef(0);

  const tick = useCallback((timestamp: number) => {
    animationFrameRef.current = requestAnimationFrame(tick);
    if (!scanActiveRef.current) {
      return;
    }
    if (!lastTimestampRef.current) {
      lastTimestampRef.current = timestamp;
      return;
    }

    const deltaTime = Math.min(
      (timestamp - lastTimestampRef.current) / 1000,
      0.05
    );
    lastTimestampRef.current = timestamp;
    scanOffsetRef.current -= SCROLL_SPEED * deltaTime;
    setScanOffset(scanOffsetRef.current);

    const scanYPosition = viewportHeightRef.current * SCAN_FRACTION;
    const lines = codeLinesRef.current;

    lines.forEach((codeLine, lineIndex) => {
      const techKey = codeLine[2];
      if (!techKey || detectedRef.current.has(techKey)) {
        return;
      }
      const top = lineIndex * LINE_HEIGHT + scanOffsetRef.current;
      if (top <= scanYPosition + 2 && top >= scanYPosition - LINE_HEIGHT + 2) {
        detectedRef.current.add(techKey);
        const generation = scanGenerationRef.current;
        setFlashingLines((prev) => new Set([...prev, lineIndex]));
        setBeamFlash(true);
        setTimeout(() => {
          if (scanGenerationRef.current !== generation) {
            return;
          }
          setFlashingLines((prev) => {
            const next = new Set(prev);
            next.delete(lineIndex);
            return next;
          });
          setHitLines((prev) => new Set([...prev, lineIndex]));
          setBeamFlash(false);
        }, 150);
        setDetectedTechs((prev) => new Set([...prev, techKey]));
      }
    });

    const detected = detectedRef.current;
    const siteTechs = techsForSiteRef.current;
    if (detected.size >= siteTechs.length && siteTechs.length > 0) {
      const lastLineY = lines.length * LINE_HEIGHT + scanOffsetRef.current;
      if (lastLineY < scanYPosition + 60) {
        scanActiveRef.current = false;
        const callback = doneCallbackRef.current;
        if (callback) {
          doneCallbackRef.current = null;
          callback();
        }
      }
    }
  }, []); // all state accessed via refs — no deps needed

  const startScan = useCallback((site: SiteData, done: () => void) => {
    scanGenerationRef.current += 1;
    detectedRef.current = new Set();
    techsForSiteRef.current = site.techs;
    // Position so the first line starts 3 lines above the scan beam
    const scanYPosition = viewportHeightRef.current * SCAN_FRACTION;
    scanOffsetRef.current = scanYPosition - LINE_HEIGHT * 3;
    lastTimestampRef.current = null;
    doneCallbackRef.current = done;

    const lines = makeCodeForSite(site);
    codeLinesRef.current = lines;

    setCurrentHost(site.host);
    setCodeLines(lines);
    setTechsForSite(site.techs);
    setDetectedTechs(new Set());
    setFlashingLines(new Set());
    setHitLines(new Set());
    setScanOffset(scanOffsetRef.current);
    scanActiveRef.current = true;
  }, []);

  const processSite = useCallback(
    (index: number, done: () => void) => {
      const rowIndex = index % MAX_URL_ROWS;
      const site = getSite(index);

      setUrlRows((prev) => {
        const next = [...prev];
        next[rowIndex] = { host: site.host, color: site.color, status: 'idle' };
        return next;
      });
      setTimeout(() => {
        setUrlRows((prev) => {
          const next = [...prev];
          next[rowIndex] = {
            host: site.host,
            color: site.color,
            status: 'active',
          };
          return next;
        });
      }, 60);

      startScan(site, () => {
        setUrlRows((prev) => {
          const next = [...prev];
          next[rowIndex] = {
            host: site.host,
            color: site.color,
            status: 'done',
          };
          return next;
        });
        done();
      });
    },
    [startScan]
  );

  const advanceRef = useRef<() => void>(() => {});
  advanceRef.current = () => {
    const index = siteIndexRef.current;
    processSite(index, () => {
      siteIndexRef.current = index + 1;
      if ((index + 1) % MAX_URL_ROWS === 0) {
        setUrlRows((prev) => prev.map((row) => ({ ...row, status: 'idle' })));
      }
      advanceRef.current();
    });
  };
  const advance = useCallback(() => advanceRef.current(), []);

  useEffect(() => {
    animationFrameRef.current = requestAnimationFrame(tick);
    advance();
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [tick, advance]);

  return {
    urlRows,
    currentHost,
    codeLines,
    scanOffset,
    flashingLines,
    hitLines,
    beamFlash,
    techsForSite,
    detectedTechs,
    viewportRef,
  };
}
